import React, { useContext, useEffect, useState } from 'react';
import {
  Grid,
  Typography,
} from '@material-ui/core';

import Alert from '@material-ui/lab/Alert';

import moment from 'moment';

import PropTypes from 'prop-types';

import AddButton from '../buttons/addButton';
import AddEventDialog from '../modals/addEventDialog';
import CustomSnackbar from '../feedback/customSnackbar';
import EventInfo from './eventInfo';
import SpinnerLoading from '../feedback/spinnerLoading';

import { getPatientInfo } from '../../utils/comms';

import translations from '../../languages/i18n';

import { SessionContext } from '../../utils/context';

export default function EventsSection({ userType, enrolmentData, eventTypes, handleReloadUserSchedules, patientData }) {
  const sessionContext = useContext(SessionContext);

  const [emptySupervisedBy, setEmptySupervisedBy] = useState(false);
  const [eventInfo, setEventInfo] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  // Només manejam amb els events del pacient que no han estat borrats
  const [patientEvents, setPatientEvents] = useState([]);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  const [exitusDate, setExitusDate] = useState('');

  const obtainEnrolmentEvents = async () => {
    // La REST no té un mètode especific per consultar els events de l'enrolment d'un pacient. S'ha de demanar tota la info del pacient
    setShowLoading(true);
    const response = await getPatientInfo(patientData._id, sessionContext.userToken);
    setShowLoading(false);
    if (response.status === 200) { // Tenim llistat
      // Només manejam amb els events del pacient que no han estat borrats
      const notDeletedEvents = response.data.patient.events.filter((element) => element.deleted === false);
      // Només manejam els events de la subscripció que estam consultant/editant
      const notDeletedProgramEvents = notDeletedEvents.filter((element) => element.enrolment === enrolmentData._id);
      setPatientEvents(notDeletedProgramEvents);

      // Si el pacient ha estat exitus es mostra la data
      if (response.data.patient.exitus) {setExitusDate(response.data.patient.exitus)};

    } else if (response === 'timeOut') { // El servidor està caigut o l'usuari no té connexió
      setFeedbackMessage(translations['_errorTimeOut']);
      setFeedbackType('error');
      setShowFeedback(true);
    } else { // Error amb les dades o al servidor
      setFeedbackMessage(translations['_errorGeneric']);
      setFeedbackType('error');
      setShowFeedback(true);
    }
  };

  // Per a que faci l'await i esperi a acabar l'execució obtainEnrolmentEvents() ho he de fer així:
  useEffect(() => {
    const initialObtainEnrolmentEvents = async () => {
      await obtainEnrolmentEvents();
    };
    initialObtainEnrolmentEvents();
    // Si ha canviat l'enrolment, miram si l'equip clínic ha deixat d'estar buit i amagam l'avís corresponent
    if (enrolmentData.supervisedBy.length !== 0) setEmptySupervisedBy(false);
  }, [enrolmentData]);

  const getDescription = (type) => {
    const description = eventTypes.find((element) => element._id === type);
    // Si l'event té traducció la mostram, sino mostram el valor del camp .desc obtingut del servidor
    if (translations[description.desc]) return translations[description.desc];
    return description.desc;
  };

  // Obtenim des d'on es pot gestionar l'event
  const getManagedBy = (eventId) => {
    const event = eventTypes.find((element) => element._id === eventId);
    return event.managedBy;
  };

  // ATENCIÓ: el servidor només té implementada la creació i eliminació d'esdeveniments.
  // De moment (25/09/20) aquesta funció no fa res, la deix al codi per si en un futur la necessit
  // const editEvent = (id) => {
  //  setEventInfo(patientEvents.find((element) => element._id === id) || null);
  //  setShowAddEvent(true);
  // };

  // Aquesta funció, també, només té sentit si es poden editar els events
  // deix el codi així per tenir-ho fet en el futur
  /* const manageAddEventAnswer = (reloadEvents) => {
    setEventInfo(null);
    setShowAddEvent(false);
    if (reloadEvents) {
      obtainEnrolmentEvents();
      handleReloadUserSchedules();
    }
  }; */

  const manageFeedback = (fbType, fbMessage, reload) => {
    setFeedbackMessage(translations[`_${fbType}${fbMessage}`]);
    setFeedbackType(fbType);
    setShowFeedback(true);
    if (reload) {
      obtainEnrolmentEvents();
      handleReloadUserSchedules();
    }
  };

  const manageShowAddEvent = () => {
    if (enrolmentData.supervisedBy.length === 0) {
      setEmptySupervisedBy(true);
    } else {
      setShowAddEvent(true);
    }
  };

  return (
    <React.Fragment>
      <SpinnerLoading show={showLoading} />
      <CustomSnackbar show={showFeedback} handleCloseFeedback={(show) => setShowFeedback(show)} type={feedbackType} message={(showFeedback && feedbackMessage) || ''} />
      {/* Pos showAddEvent && per evitar que s'executi AddEventDialog fins que no obro el AddEvent */}
      {showAddEvent && (
        <AddEventDialog
          open={showAddEvent}
          title={translations['_addEventDialogTitle']}
          enrolmentData={enrolmentData}
          eventTypes={eventTypes}
          eventInfo={eventInfo}
          idUser={patientData._id}
          // En cas d'implementar l'edició d'events emprar aquesta:
          // handleClose={(reloadEvents) => manageAddEventAnswer(reloadEvents)}
          handleClose={() => setShowAddEvent(false)}
          handleFeedback={(fbType, fbMessage, reload) => manageFeedback(fbType, fbMessage, reload)}
        />
      )}
      <Grid container justify='flex-start' spacing={2}>
        <Grid item xs={10}>
          <Typography variant='h6' component='div'>{translations['_events']}</Typography>
        </Grid>
        {(patientEvents.length !== 0 && (
          patientEvents.map((event) => (
            <Grid item xs={10} key={`grid${event._id}`}>
              <EventInfo
                key={event._id}
                description={getDescription(event.type)}
                idUser={patientData._id}
                patientEventData={event}
                // handleEditEvent={(id) => editEvent(id)}
                handleFeedback={(fbType, fbMessage, reload) => manageFeedback(fbType, fbMessage, reload)}
                userType={userType}
                managedBy={getManagedBy(event.type)}
              />
            </Grid>
          ))
        )) || (
          <Grid item xs={10}>
            <Typography variant='body1' component='div'>{translations['_emptyList']}</Typography>
          </Grid>
        )}
        <Grid item xs={10}>
            <Typography style={{ color: exitusDate ? 'red' :'green', fontWeight: 'bold', fontSize: 18 }} component='div'>{translations['_exitus']} {exitusDate ? moment(exitusDate).format('DD/MM/YYYY') : translations['_exitusNotNotified']} </Typography>
        </Grid>

        {emptySupervisedBy && (
          <Grid item xs={5}>
            <Alert severity='error'>{translations['_emptySupervisedBy']}</Alert>
          </Grid>
        )}
        <Grid item xs={10}>
          {/* Si el clínic es de tipus bàsic o l'administrador del programa no pot afegir events */}
          {/* TODO: decidir què fer amb l'usuari Admin */}
          <AddButton disabled={userType === 'Basic' || userType === 'Administrator'} handleClick={manageShowAddEvent} />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

// Indica i controla els props que se han de passar al component
EventsSection.propTypes = {
  enrolmentData: PropTypes.object.isRequired,
  eventTypes: PropTypes.array.isRequired,
  handleReloadUserSchedules: PropTypes.func.isRequired,
  patientData: PropTypes.object.isRequired,
  userType: PropTypes.string.isRequired,
};
